// frontend/src/components/Navbars/AdminNavbar.jsx - ACTUALIZADO CON LINKS A CASHFLOW

import React, { useState, useEffect } from 'react';
import {
  Navbar,
  Container,
  Nav,
  UncontrolledDropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
  InputGroup,
  InputGroupText,
  Input,
  Badge,
} from 'reactstrap';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { usersAPI, invoiceSchedulesAPI, cashflowAPI } from '../../services/api';

function AdminNavbar({ brandText }) {
  const { user, logout, hasPermission } = useAuth();
  const navigate = useNavigate();
  
  // Estados para notificaciones de usuarios
  const [pendingUsersCount, setPendingUsersCount] = useState(0);
  const [userNotifications, setUserNotifications] = useState([]);
  
  // ✅ Estados para notificaciones de facturación
  const [pendingInvoicesCount, setPendingInvoicesCount] = useState(0);
  const [overdueInvoicesCount, setOverdueInvoicesCount] = useState(0);
  const [totalInvoiceNotifications, setTotalInvoiceNotifications] = useState(0);

  // Cargar usuarios pendientes si es admin
  useEffect(() => {
    if (user && (user.role === 'super_admin' || hasPermission('approve_users') || hasPermission('manage_users'))) {
      loadPendingUsers();
      const interval = setInterval(loadPendingUsers, 30000);
      return () => clearInterval(interval);
    }
  }, [user]);

  // ✅ Cargar notificaciones de facturación
  useEffect(() => {
    if (!user) return;

    const canViewFinance = hasPermission('view_finance');
    const canCreateInvoices = hasPermission('create_invoices');
    const isFinanceRole = ['super_admin', 'admin', 'finance'].includes(user.role);
    
    console.log('🔍 Verificando permisos de facturación:', {
      userEmail: user.email,
      userRole: user.role,
      permissions: user.permissions,
      canViewFinance,
      canCreateInvoices,
      isFinanceRole,
      shouldLoad: canViewFinance || canCreateInvoices || isFinanceRole,
    });

    if (canViewFinance || canCreateInvoices || isFinanceRole) {
      console.log('✅ Cargando notificaciones de facturación...');
      loadInvoiceNotifications();
      const interval = setInterval(loadInvoiceNotifications, 60000);
      return () => clearInterval(interval);
    } else {
      console.log('❌ Usuario sin permisos de facturación');
    }
  }, [user, hasPermission]);


  const loadPendingUsers = async () => {
    try {
      const response = await usersAPI.getPending();
      const pending = response.data.users || [];
      setPendingUsersCount(pending.length);
      
      const newNotifications = pending.slice(0, 3).map(u => ({
        id: u.id,
        type: 'user_pending',
        title: 'Nuevo usuario pendiente',
        message: `${u.full_name} solicita acceso`,
        time: new Date(u.created_at),
        user: u,
      }));
      
      setUserNotifications(newNotifications);
    } catch (error) {
      console.error('Error cargando usuarios pendientes:', error);
    }
  };

  const loadInvoiceNotifications = async () => {
    try {
      console.log('📡 Llamando a /api/finance/invoice-schedules/notifications-count/');
      const response = await invoiceSchedulesAPI.getNotificationsCount();
      console.log('📥 Respuesta recibida:', response.data);
      
      const data = response.data;
      
      setPendingInvoicesCount(data.pending || 0);
      setOverdueInvoicesCount(data.overdue || 0);
      setTotalInvoiceNotifications(data.count || 0);
      
      console.log('✅ Notificaciones actualizadas:', {
        pending: data.pending,
        overdue: data.overdue,
        total: data.count,
      });
    } catch (error) {
      console.error('❌ Error cargando notificaciones:', error);
      console.error('   Response:', error.response?.data);
    }
  };


  const handleNotificationClick = (notification) => {
    if (notification.type === 'user_pending') {
      navigate('/admin/users');
    } else if (notification.type === 'invoice_pending') {
      navigate('/admin/finance/inbox');
    }
  };

  const getTimeAgo = (date) => {
    const seconds = Math.floor((new Date() - new Date(date)) / 1000);
    
    if (seconds < 60) return 'Hace un momento';
    if (seconds < 3600) return `Hace ${Math.floor(seconds / 60)} minutos`;
    if (seconds < 86400) return `Hace ${Math.floor(seconds / 3600)} horas`;
    return `Hace ${Math.floor(seconds / 86400)} días`;
  };

// frontend/src/components/Navbars/AdminNavbar.jsx (continuación)

  // ✅ Calcular total de notificaciones
  const totalNotifications = pendingUsersCount + totalInvoiceNotifications;

  return (
    <Navbar
      className="navbar navbar-main navbar-expand-lg px-0 mx-4 shadow-none border-radius-xl"
      id="navbarBlur"
      expand="lg"
    >
      <Container fluid className="py-1 px-3">
        <nav aria-label="breadcrumb">
          <ol className="breadcrumb bg-transparent mb-0 pb-0 pt-1 px-0 me-sm-6 me-5">
            <li className="breadcrumb-item text-sm">
              <a className="opacity-5 text-white" href="#pages">
                Páginas
              </a>
            </li>
            <li className="breadcrumb-item text-sm text-white active" aria-current="page">
              {brandText}
            </li>
          </ol>
          <h6 className="font-weight-bolder text-white mb-0">{brandText}</h6>
        </nav>

        <div className="collapse navbar-collapse mt-sm-0 mt-2 me-md-0 me-sm-4" id="navbar">
          <div className="ms-md-auto pe-md-3 d-flex align-items-center">
            <InputGroup>
              <InputGroupText className="text-body">
                <i className="fas fa-search" aria-hidden="true"></i>
              </InputGroupText>
              <Input type="text" className="form-control" placeholder="Buscar..." />
            </InputGroup>
          </div>

          <Nav className="justify-content-end" navbar>
            {/* ✅ NOTIFICACIONES CON BADGE */}
            <UncontrolledDropdown nav className="pe-2 d-flex align-items-center">
              <DropdownToggle nav className="text-white p-0 position-relative" id="dropdownMenuButton">
                <i className="fa fa-bell cursor-pointer"></i>
                {totalNotifications > 0 && (
                  <Badge 
                    color={overdueInvoicesCount > 0 ? 'danger' : 'warning'} 
                    pill 
                    className="position-absolute top-0 start-100 translate-middle"
                    style={{ fontSize: '0.65rem' }}
                  >
                    {totalNotifications}
                  </Badge>
                )}
              </DropdownToggle>
              <DropdownMenu end className="dropdown-menu-arrow px-2 py-3 me-sm-n4" style={{ minWidth: '320px' }}>
                <DropdownItem header className="text-center">
                  <strong>Notificaciones</strong>
                  {totalNotifications > 0 && (
                    <Badge color="warning" pill className="ms-2">
                      {totalNotifications} nuevas
                    </Badge>
                  )}
                </DropdownItem>
                <DropdownItem divider />
                
                {totalNotifications === 0 ? (
                  <DropdownItem className="text-center text-muted">
                    <i className="ni ni-check-bold me-2"></i>
                    No hay notificaciones
                  </DropdownItem>
                ) : (
                  <>
                    {/* ✅ Notificaciones de Facturación */}
                    {totalInvoiceNotifications > 0 && (
                      <>
                        <DropdownItem header className="text-xs text-uppercase text-muted">
                          Facturación
                        </DropdownItem>
                        
                        {/* ✅ Facturas Vencidas - Link a Cashflow */}
                        {overdueInvoicesCount > 0 && (
                          <DropdownItem 
                            className="mb-2"
                            onClick={() => navigate('/admin/finance/cashflow')}
                          >
                            <div className="d-flex py-1">
                              <div className="my-auto">
                                <div className="avatar avatar-sm bg-gradient-danger me-3 d-flex align-items-center justify-content-center">
                                  <i className="ni ni-bell-55 text-white"></i>
                                </div>
                              </div>
                              <div className="d-flex flex-column justify-content-center">
                                <h6 className="text-sm font-weight-normal mb-1">
                                  <span className="font-weight-bold text-danger">Facturas Vencidas</span>
                                </h6>
                                <p className="text-xs text-secondary mb-0">
                                  {overdueInvoicesCount} factura{overdueInvoicesCount > 1 ? 's' : ''} vencida{overdueInvoicesCount > 1 ? 's' : ''} - Requiere atención
                                </p>
                              </div>
                            </div>
                          </DropdownItem>
                        )}
                        
                        {/* ✅ Facturas Pendientes - Link a Inbox */}
                        {pendingInvoicesCount > 0 && (
                          <DropdownItem 
                            className="mb-2"
                            onClick={() => navigate('/admin/finance/inbox')}
                          >
                            <div className="d-flex py-1">
                              <div className="my-auto">
                                <div className="avatar avatar-sm bg-gradient-warning me-3 d-flex align-items-center justify-content-center">
                                  <i className="ni ni-calendar-grid-58 text-white"></i>
                                </div>
                              </div>
                              <div className="d-flex flex-column justify-content-center">
                                <h6 className="text-sm font-weight-normal mb-1">
                                  <span className="font-weight-bold">Facturas Pendientes</span>
                                </h6>
                                <p className="text-xs text-secondary mb-0">
                                  {pendingInvoicesCount} factura{pendingInvoicesCount > 1 ? 's' : ''} por generar
                                </p>
                              </div>
                            </div>
                          </DropdownItem>
                        )}
                        
                        {/* ✅ Link rápido a Dashboard de Finanzas */}
                        <DropdownItem 
                          className="mb-2 text-center"
                          onClick={() => navigate('/admin/finance')}
                        >
                          <small className="text-primary">
                            <i className="ni ni-chart-bar-32 me-1"></i>
                            Ver Dashboard de Finanzas
                          </small>
                        </DropdownItem>
                        
                        <DropdownItem divider />
                      </>
                    )}
                    
                    {/* Notificaciones de Usuarios */}
                    {userNotifications.length > 0 && (
                      <>
                        <DropdownItem header className="text-xs text-uppercase text-muted">
                          Usuarios
                        </DropdownItem>
                        {userNotifications.map((notif) => (
                          <DropdownItem 
                            key={notif.id}
                            className="mb-2"
                            onClick={() => handleNotificationClick(notif)}
                          >
                            <div className="d-flex py-1">
                              <div className="my-auto">
                                <div className="avatar avatar-sm bg-gradient-info me-3 d-flex align-items-center justify-content-center">
                                  <i className="ni ni-single-02 text-white"></i>
                                </div>
                              </div>
                              <div className="d-flex flex-column justify-content-center">
                                <h6 className="text-sm font-weight-normal mb-1">
                                  <span className="font-weight-bold">{notif.title}</span>
                                </h6>
                                <p className="text-xs text-secondary mb-0">
                                  {notif.message}
                                </p>
                                <p className="text-xs text-secondary mb-0">
                                  <i className="fa fa-clock me-1"></i>
                                  {getTimeAgo(notif.time)}
                                </p>
                              </div>
                            </div>
                          </DropdownItem>
                        ))}
                      </>
                    )}
                  </>
                )}
              </DropdownMenu>
            </UncontrolledDropdown>

            {/* PERFIL DE USUARIO */}
            <UncontrolledDropdown nav className="pe-2 d-flex align-items-center">
              <DropdownToggle nav className="text-white p-0" id="dropdownUser">
                <div className="d-flex align-items-center">
                  {user?.profile_picture ? (
                    <img 
                      src={user.profile_picture} 
                      alt={user.first_name}
                      className="avatar avatar-sm rounded-circle me-2"
                    />
                  ) : (
                    <div className="avatar avatar-sm bg-gradient-primary rounded-circle me-2 d-flex align-items-center justify-content-center">
                      <i className="ni ni-single-02 text-white"></i>
                    </div>
                  )}
                  <span className="d-sm-inline d-none">
                    {user?.first_name || 'Usuario'}
                  </span>
                </div>
              </DropdownToggle>
              <DropdownMenu end className="dropdown-menu-arrow px-2 py-3">
                <DropdownItem header className="text-center">
                  <strong>{user?.first_name} {user?.last_name}</strong>
                  <br />
                  <small className="text-muted">{user?.email}</small>
                  <br />
                  <Badge color="info" className="mt-2">
                    {user?.role}
                  </Badge>
                </DropdownItem>
                <DropdownItem divider />
                <DropdownItem href="#profile" onClick={(e) => e.preventDefault()}>
                  <i className="ni ni-single-02 me-2"></i>
                  Mi Perfil
                </DropdownItem>
                <DropdownItem href="#settings" onClick={(e) => e.preventDefault()}>
                  <i className="ni ni-settings-gear-65 me-2"></i>
                  Configuración
                </DropdownItem>
                <DropdownItem divider />
                <DropdownItem onClick={logout} className="text-danger">
                  <i className="ni ni-user-run me-2"></i>
                  Cerrar Sesión
                </DropdownItem>
              </DropdownMenu>
            </UncontrolledDropdown>

            {/* TOGGLE SIDEBAR (MOBILE) */}
            <li className="nav-item d-xl-none ps-3 d-flex align-items-center">
              <a
                href="#sidenav"
                className="nav-link text-white p-0"
                id="iconNavbarSidenav"
                onClick={(e) => {
                  e.preventDefault();
                  document.body.classList.toggle('g-sidenav-pinned');
                  document.body.classList.toggle('g-sidenav-hidden');
                }}
              >
                <div className="sidenav-toggler-inner">
                  <i className="sidenav-toggler-line bg-white"></i>
                  <i className="sidenav-toggler-line bg-white"></i>
                  <i className="sidenav-toggler-line bg-white"></i>
                </div>
              </a>
            </li>
          </Nav>
        </div>
      </Container>
    </Navbar>
  );
}

export default AdminNavbar;
